import { Injectable } from '@angular/core';
import lunr from 'lunr';

@Injectable({
    providedIn: 'root',
})
export class FuzzySearchService {
    private indexes: { [key: string]: lunr.Index } = {};

    constructor() { }

    createIndex(type: string, data: string[]) {
        this.indexes[type] = lunr(function () {
            this.ref('id');
            this.field('name', { boost: 10, wildcard: lunr.Query.wildcard.TRAILING });

            const that = this;

            data.forEach((item, index) => {
                that.add({ id: index, name: item });
            });
        });
    }

    searchInDataset(query: string, dataset: Record<string, string[]>): { name: string, type: string }[] {
        Object.keys(dataset).forEach((key) => {
            this.createIndex(key, dataset[key]);
        });

        const searchWords = query.toLowerCase().split(/\s+/);

        let bestMatches: { name: string, type: string, score: number }[] = [];

        Object.keys(this.indexes).forEach((type) => {
            const index = this.indexes[type];

            if (!index) return;

            const results = index.search(query);
            results.forEach(result => {
                const matchedItem = dataset[type][Number(result.ref)];

                if (searchWords.some(word => matchedItem.toLowerCase().includes(word))) {
                    bestMatches.push({ name: matchedItem, type, score: result.score });
                }
            });
        });

        const finalResults = Object.values(
            bestMatches.reduce((acc, item) => {
                if (!acc[item.type] || acc[item.type].score < item.score) {
                    acc[item.type] = item;
                }
                return acc;
            }, {} as Record<string, { name: string, type: string, score: number }>)
        );

        return finalResults.map(({ name, type }) => ({ name, type }));
    }

    searchInArray(query: string, list: string[]): string[] {
        const index = lunr(function () {
            this.ref('id');
            this.field('name');

            list.forEach((name, id) => {
                this.add({ id, name });
            });
        });

        const matches = index.search(query + '~1'); // Fuzzy match
        return matches.map((match) => list[parseInt(match.ref, 10)]);
    }

    fuzzyMatchInString(query: string, text: string): boolean {
        const words = text.split(/\s+/);
        return words.some((word) => this.levenshteinDistance(query.toLowerCase(), word.toLowerCase()) <= 2);
    }

    private levenshteinDistance(a: string, b: string): number {
        const dp: number[][] = Array.from({ length: a.length + 1 }, () =>
            Array(b.length + 1).fill(0)
        );

        for (let i = 0; i <= a.length; i++) dp[i][0] = i;
        for (let j = 0; j <= b.length; j++) dp[0][j] = j;

        for (let i = 1; i <= a.length; i++) {
            for (let j = 1; j <= b.length; j++) {
                dp[i][j] =
                    a[i - 1] === b[j - 1]
                        ? dp[i - 1][j - 1]
                        : Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]) + 1;
            }
        }

        return dp[a.length][b.length];
    }

}

